import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { BrandLogo } from "../../components";
import ThemeToggle from "../ThemeToggle";
import { UserMenu } from "./UserMenu";
import { ROUTES } from "../../utils";
import { LeftPanelCloseIcon, LeftPanelOpenIcon } from "../../../assets/icons";
import { useClientScope } from "../../contexts";
import yodhaLogo from "../../../assets/yodha-logo.png";

interface AdminAppHeaderProps {
  onMenu: () => void;
  drawerOpen: boolean;
}

const navigationSections = [
  {
    title: "WELLNESS & HEALTH",
    items: [
      {
        text: "Programme Profile",
        path: ROUTES.ADMIN_DASHBOARD,
      },
      {
        text: "Wellness Overview",
        path: "/admin/wellness-overview",
      },
      {
        text: "Recovery",
        path: "/admin/recovery",
      },
      {
        text: "Sleep Analytics",
        path: "/admin/sleep-analytics",
      },
      {
        text: "Strain and Activity",
        path: "/admin/strain-activity",
      },
      {
        text: "Overall Physiological Stress",
        path: "/admin/stress-mind",
      },
      {
        text: "Biometrics and Vitals",
        path: "/admin/biometrics",
      },
      {
        text: "User Progress",
        path: "/admin/user-progress",
      },
      {
        text: "Team Progress",
        path: "/admin/team-progress",
      },
      {
        text: "Jeopardy Insights",
        path: "/admin/jeopardy-insights",
      },
      {
        text: "Measure Comparison In...",
        path: "/admin/measure-comparison",
      },
      {
        text: "Predictive Insights",
        path: "/admin/predictive-insights",
      },
    ],
  },
  /*
  {
    title: "WELLNESS PROGRAMS",
    items: [
      {
        text: "Learning Resources",
        path: "/admin/learning-resources",
      },
      {
        text: "Certificates & Badges",
        path: "/admin/certificates-badges",
      },
      {
        text: "Programs & Challenges",
        path: "/admin/programs-challenges",
      },
    ],
  },
  */
  {
    title: "ADMINISTRATION",
    items: [
      {
        text: "Participants",
        path: ROUTES.PARTICIPANTS,
      },
      {
        text: "Participant Settings",
        path: ROUTES.PARTICIPANT_SETTINGS,
      },
      {
        text: "Pre-Registrations",
        path: ROUTES.PRE_REGISTRATIONS,
      },
      {
        text: "Wellbeing Content",
        path: ROUTES.WELLBEING_CONTENT,
      },
      {
        text: "Settings",
        path: ROUTES.SETTINGS,
      },
    ],
  },
];

export const AdminAppHeader = ({ onMenu, drawerOpen }: AdminAppHeaderProps) => {
  const location = useLocation();
  const {
    isCentralAdmin,
    clients,
    selectedClientId,
    setSelectedClientId,
  } = useClientScope();

  const navItems = navigationSections.flatMap((section) =>
    section.items.filter((item) => {
      const isParticipantsLink =
        item.path === ROUTES.PARTICIPANTS ||
        item.path === ROUTES.PARTICIPANT_SETTINGS;
      return isParticipantsLink ? isCentralAdmin : true;
    })
  );
  const directMatch = navItems.find((item) => item.path === location.pathname);
  const participantMatch = location.pathname.startsWith(`${ROUTES.PARTICIPANTS}/`)
    ? navItems.find((item) => item.path === ROUTES.PARTICIPANTS)
    : null;
  const currentPage = directMatch || participantMatch || navItems[0];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 3,
        py: 1,
        position: "relative",
        zIndex: 10,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <IconButton edge="start" onClick={onMenu} sx={{ mr: 2 }}>
        {drawerOpen ? <LeftPanelCloseIcon /> : <LeftPanelOpenIcon />}
      </IconButton>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {currentPage.text}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        {isCentralAdmin && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedClientId}
              displayEmpty
              onChange={(event) => setSelectedClientId(event.target.value)}
              sx={{ fontSize: 13 }}
              disabled={!clients.length}
              renderValue={(value) => {
                const selected = clients.find((client) => client.id === value);
                return selected?.name || "All clients";
              }}
            >
              <MenuItem value="">
                <em>All clients</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem value={client.id} key={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          component={RouterLink}
          to={ROUTES.FAQ}
          color="inherit"
          size="small"
        >
          FAQ
        </Button>
        <UserMenu />
        <ThemeToggle />
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
            mx: 1,
          }}
        >
          <Tooltip title="Open Yodha" placement="bottom">
            <Box
              component="a"
              href="https://oh.yodha.io/"
              target="_blank"
              rel="noreferrer"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                "& img": {
                  width: 30,
                  height: 30,
                  borderRadius: "4px",
                  display: "block",
                },
              }}
              aria-label="Open Yodha"
            >
              <Box component="img" src={yodhaLogo} alt="Yodha" />
            </Box>
          </Tooltip>
          <Box
            component={RouterLink}
            to={ROUTES.LOGIN}
            sx={{
              maxWidth: "112px",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              textDecoration: "none",
              color: "inherit",
              "& svg, & img": {
                maxWidth: "112px",
                maxHeight: "37px",
              },
            }}
          >
            <BrandLogo />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
